import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { markVisitorNdaSigned } from "./fibery";
import "./NdaSign.css";

// NDA Content with [SIG] placeholder for signature
const NDA_CONTENT = `
**SOUND DISPOSITION LTD**

Tel: +44 208 801 9190
Fax: +44 203 137 9190
info@sounddisposition.com
www.sounddisposition.com

---

**MUTUAL CONFIDENTIALITY AGREEMENT**

THIS AGREEMENT is dated **[DATE]**

**BETWEEN**

(1) **SOUND DISPOSITION LTD**, a company incorporated and registered in England under company number 06473473, and whose registered offices are at 31 Millmead Business Centre, Millmead Rd, London N17 9QU (**Company**); and

(2) **[NAME]** (**Individual**).

**Purpose for Disclosure ("Business Purpose"):** The parties wish to disclose or make available certain Confidential Information (as defined below) to each other with a view to entering into discussions relating to a potential new business relationship and/or any services or products that could be offered or received by either of the parties

**IT IS HEREBY AGREED:**

1. For purposes of this Agreement, "Confidential Information" shall mean any and all non-public information, including, without limitation, technical, developmental, marketing, sales, operating, performance, cost, know-how, business plans, business methods, and process information, disclosed to the Recipient. For convenience, the Disclosing Party may, but is not required to, mark written Confidential Information with the legend "Confidential" or an equivalent designation.

2. All Confidential Information disclosed to the Recipient will be used solely for the Business Purpose and for no other purpose whatsoever. The Recipient agrees to keep the Disclosing Party's Confidential Information confidential and to protect the confidentiality of such Confidential Information with the same degree of care with which it protects the confidentiality of its own confidential information, but in no event with less than a reasonable degree of care. Recipient may disclose Confidential Information only to its employees, agents, consultants and contractors on a need-to-know basis, and only if such employees, agents, consultants and contractors have executed appropriate written agreements with Recipient sufficient to enable Recipient to enforce all the provisions of this Agreement. Recipient shall not make any copies of Disclosing Party's Confidential Information except as needed for the Business Purpose. At the request of Disclosing Party, Recipient shall return to Disclosing Party all Confidential Information of Disclosing Party (including any copies thereof) or certify the destruction thereof.

3. All right title and interest in and to the Confidential Information shall remain with Disclosing Party or its licensors. Nothing in this Agreement is intended to grant any rights to Recipient under any patents, copyrights, trademarks, or trade secrets of Disclosing Party.

4. The obligations and limitations set forth herein regarding Confidential Information shall not apply to information which is: (a) at any time in the public domain, other than by a breach on the part of the Recipient; or (b) at any time rightfully received from a third party which had the right to and transmits it to the Recipient without any obligation of confidentiality.

5. In the event that the Recipient shall breach this Agreement, or in the event that a breach appears to be imminent, the Disclosing Party shall be entitled to all legal and equitable remedies afforded it by law, and in addition may recover all reasonable costs and attorneys' fees incurred in seeking such remedies. If the Confidential Information is sought by any third party, including by way of subpoena or other court process, the Recipient shall inform the Disclosing Party of the request in sufficient time to permit the Disclosing Party to object to and, if necessary, seek court intervention to prevent the disclosure.

6. Governing law and jurisdiction

6.1 This Agreement and any dispute or claim arising out of or in connection with it (including non-contractual disputes or claims) shall be governed by and construed in accordance with English law.

6.2 The parties irrevocably agree that the courts of England and Wales shall have exclusive jurisdiction to settle any dispute or claim that arises out of or in connection with this Agreement.

---

IN WITNESS WHEREOF, the Parties have executed this Agreement as of the date first above written.

Signature: [SIG]

Name: [NAME]
`;

function NdaSign() {
    const navigate = useNavigate();
    const [firstName, setFirstName] = useState("");
    const [surname, setSurname] = useState("");
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState(false);

    const handleBack = () => {
        navigate(-1);
    };

    const getCurrentDate = () => {
        const today = new Date();
        return today.toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
        });
    };

    const generatePDF = async () => {
        if (!firstName.trim() || !surname.trim()) {
            setError("Please enter both first name and surname");
            return;
        }

        if (!email.trim()) {
            setError(
                "Please enter your email address to receive the signed NDA",
            );
            return;
        }

        setError("");
        setLoading(true);

        try {
            // Concatenate first name and surname for display
            const fullName = `${firstName.trim()} ${surname.trim()}`;

            // Create a temporary container to render the NDA with signature
            const tempContainer = document.createElement("div");
            tempContainer.style.position = "absolute";
            tempContainer.style.left = "-10000px";
            tempContainer.style.width = "8.5in";
            tempContainer.style.padding = "20px";
            tempContainer.style.backgroundColor = "white";
            tempContainer.style.fontFamily = "Arial, sans-serif";
            tempContainer.style.fontSize = "11px";
            tempContainer.style.lineHeight = "1.5";
            tempContainer.style.color = "#000";

            const currentDate = getCurrentDate();
            // Replace placeholders with visitor info
            let ndaContent = NDA_CONTENT.replace(/\[DATE\]/g, currentDate)
                .replace(/\[NAME\]/g, fullName)
                .replace(/\[SIG\]/g, fullName);

            // Convert markdown to simple HTML
            const htmlContent = ndaContent
                .split("\n")
                .map((line) => {
                    // Skip lines starting with "Date:"
                    if (line.trim().startsWith("Date:")) {
                        return "";
                    } else if (line.startsWith("**") && line.endsWith("**")) {
                        // Bold headings
                        return `<p style="font-weight: bold; margin-top: 12px; margin-bottom: 8px;">${line
                            .replace(/\*\*/g, "")
                            .trim()}</p>`;
                    } else if (line.match(/^\d+\./)) {
                        // Numbered sections
                        return `<p style="margin-top: 8px; margin-bottom: 8px;">${line}</p>`;
                    } else if (line.trim() === "") {
                        return "";
                    } else if (
                        line.includes("Signature:") &&
                        line.includes(fullName)
                    ) {
                        // Render signature in Caveat font
                        return `<p style="margin: 4px 0; margin-top: 20px;">Signature: <span style="font-family: 'Caveat', cursive; font-size: 28px; font-weight: 700; display: inline-block;">${fullName}</span></p>`;
                    } else {
                        return `<p style="margin: 4px 0;">${line}</p>`;
                    }
                })
                .join("");

            tempContainer.innerHTML = `
        <div>
          ${htmlContent}
        </div>
      `;

            document.body.appendChild(tempContainer);

            // Convert to canvas and then PDF
            const canvas = await html2canvas(tempContainer, {
                scale: 2,
                useCORS: true,
                backgroundColor: "#ffffff",
                allowTaint: true,
            });

            document.body.removeChild(tempContainer);

            const imgData = canvas.toDataURL("image/png");
            const pdf = new jsPDF("p", "mm", "a4");

            const imgWidth = 210; // A4 width in mm
            const imgHeight = (canvas.height * imgWidth) / canvas.width;
            let heightLeft = imgHeight;
            let position = 0;

            pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
            heightLeft -= 297; // A4 height in mm

            while (heightLeft >= 0) {
                position = heightLeft - imgHeight;
                pdf.addPage();
                pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
                heightLeft -= 297;
            }

            // Get PDF as blob - convert to arrayBuffer for reusability
            const pdfArrayBuffer = pdf.output("arraybuffer");
            const pdfBlob = new Blob([pdfArrayBuffer], {
                type: "application/pdf",
            });

            // Send PDF to email via proxy
            const formattedDate = currentDate.replace(/\s+/g, "_");
            const params = new URLSearchParams({
                firstName: firstName.trim(),
                surname: surname.trim(),
                email: email.trim(),
                date: formattedDate,
            });

            // Create a new blob from the arrayBuffer for email (to avoid blob reuse issues)
            const emailBlob = new Blob([pdfArrayBuffer], {
                type: "application/pdf",
            });

            const response = await fetch(
                `/send-nda-email?${params.toString()}`,
                {
                    method: "POST",
                    body: emailBlob,
                    headers: {
                        "Content-Type": "application/octet-stream",
                    },
                },
            );

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.details || "Failed to send email");
            }

            // Try to update visitor NDA status in Fibery and upload PDF
            try {
                console.log("=== MARKING VISITOR NDA AS SIGNED ===");
                console.log(
                    `Lookup params: firstName=${firstName.trim()}, surname=${surname.trim()}, email=${email.trim()}`,
                );
                const updatedVisitor = await markVisitorNdaSigned(
                    firstName.trim(),
                    surname.trim(),
                    email.trim(),
                );

                if (updatedVisitor) {
                    console.log(
                        "✓ Visitor NDA status updated successfully:",
                        updatedVisitor,
                    );
                    console.log("About to attempt PDF upload...");
                    console.log("pdfBlob defined?", !!pdfBlob);
                    console.log(
                        "pdfBlob size:",
                        pdfBlob ? pdfBlob.size : "N/A",
                    );

                    // Upload PDF to Fibery
                    try {
                        console.log("=== UPLOADING NDA PDF TO FIBERY ===");
                        console.log(
                            `Uploading PDF for visitor ID: ${updatedVisitor.id}`,
                        );
                        console.log(`PDF blob size: ${pdfBlob.size} bytes`);

                        const uploadParams = new URLSearchParams({
                            visitorId: updatedVisitor.id,
                        });

                        const uploadUrl = `/upload-nda-to-fibery?${uploadParams.toString()}`;
                        console.log(`Upload URL: ${uploadUrl}`);

                        const uploadResponse = await fetch(uploadUrl, {
                            method: "POST",
                            body: pdfBlob,
                            headers: {
                                "Content-Type": "application/octet-stream",
                            },
                        });

                        console.log(
                            `Upload response status: ${uploadResponse.status}`,
                        );

                        if (!uploadResponse.ok) {
                            const uploadText = await uploadResponse.text();
                            console.warn(
                                "⚠ Failed to upload PDF to Fibery:",
                                uploadText,
                            );
                            // Don't fail - email was already sent
                        } else {
                            const uploadData = await uploadResponse.json();
                            console.log(
                                "✓ NDA PDF uploaded and attached to Fibery successfully",
                                uploadData,
                            );
                        }
                    } catch (uploadErr) {
                        console.warn(
                            "⚠ Error uploading PDF to Fibery:",
                            uploadErr.message,
                        );
                        console.warn(uploadErr);
                        // Don't fail - email was already sent
                    }
                } else {
                    console.log(
                        "⚠ Visitor not found in database - NDA status not updated",
                    );
                }
            } catch (fiberyErr) {
                console.error("✗ Error updating Fibery:", fiberyErr.message);
                // Don't fail the whole process if Fibery update fails
            }

            setSuccess(true);

            // Reset form and redirect after 3 seconds
            setTimeout(() => {
                setFirstName("");
                setSurname("");
                setEmail("");
                setSuccess(false);
                navigate("/");
            }, 3000);
        } catch (err) {
            console.error("Error generating PDF:", err);
            setError("Failed to send NDA. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="nda-sign-page">
            <header className="nda-header">
                <button className="back-button" onClick={handleBack}>
                    Back
                </button>
                <h1 className="nda-title">Non-Disclosure Agreement</h1>
            </header>

            <div className="nda-main">
                    <div className="nda-document">
                        {NDA_CONTENT.split("\n").map((line, idx) => {
                            if (line.startsWith("**") && line.endsWith("**")) {
                                return (
                                    <h2
                                        key={idx}
                                        className="nda-section-heading"
                                    >
                                        {line.replace(/\*\*/g, "").trim()}
                                    </h2>
                                );
                            } else if (line.match(/^\d+\./)) {
                                return (
                                    <p
                                        key={idx}
                                        className="nda-numbered-section"
                                    >
                                        {line}
                                    </p>
                                );
                            } else if (line.trim() === "") {
                                return null;
                            } else {
                                return (
                                    <p key={idx} className="nda-text">
                                        {line}
                                    </p>
                                );
                            }
                        })}
                    </div>

                    <div className="nda-signature-form">
                        <h3>Sign Below</h3>

                        {error && <div className="error-message">{error}</div>}
                        {success && (
                            <div className="success-message">
                                ✓ NDA sent to your email successfully!
                                Redirecting...
                            </div>
                        )}

                        <div className="form-group">
                            <label htmlFor="firstName">First Name *</label>
                            <input
                                type="text"
                                id="firstName"
                                value={firstName}
                                onChange={(e) => {
                                    setFirstName(e.target.value);
                                    setError("");
                                }}
                                placeholder="Enter your first name"
                                disabled={loading}
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="surname">Surname *</label>
                            <input
                                type="text"
                                id="surname"
                                value={surname}
                                onChange={(e) => {
                                    setSurname(e.target.value);
                                    setError("");
                                }}
                                placeholder="Enter your surname"
                                disabled={loading}
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="email">Email *</label>
                            <input
                                type="email"
                                id="email"
                                value={email}
                                onChange={(e) => {
                                    setEmail(e.target.value);
                                    setError("");
                                }}
                                placeholder="Enter your email address"
                                disabled={loading}
                            />
                        </div>

                        <div className="date-display">
                            <strong>Date:</strong> {getCurrentDate()}
                        </div>

                        <button
                            className="sign-button"
                            onClick={generatePDF}
                            disabled={
                                loading ||
                                !firstName.trim() ||
                                !surname.trim() ||
                                !email.trim()
                            }
                        >
                            {loading ? "Sending PDF..." : "Sign & Send NDA"}
                        </button>

                        <p className="form-note">
                            By clicking the sign button, your signed NDA will be
                            generated and sent to your email.
                        </p>
                    </div>
            </div>
        </div>
    );
}

export default NdaSign;
